import {
  formatOfferCountdown,
  useWinBackOffer,
} from "../Hooks/WinBackOfferContext";

/**
 * WinBackOfferBanner — slim app-wide strip shown while a win-back offer is live.
 *
 *   "Extra 5% off — 47:12 left"   [Shop now]
 *
 * PINNED TO THE TOP, DELIBERATELY. Both of the things it must not cover are bottom-anchored:
 * the PDP's sticky Add-to-cart / notify bar is the last flex child of a `h-[100dvh]` column,
 * and the draggable floating video card sits in an absolutely-positioned bottom-right column
 * offset by the sticky bar's measured height (`fabBottom`). A bottom banner would land squarely
 * on both. Top is the only edge that is free on every route.
 *
 * `fixed` rather than in-flow: the PDP root is `h-[100dvh] overflow-hidden`, so a banner in
 * normal flow above the router outlet would make the document taller than the viewport and
 * introduce an outer scrollbar on a page designed to scroll internally.
 *
 * SPACE IS RESERVED, NOT OVERLAID. `fixed` alone took the bar out of flow, which meant it sat on
 * top of whatever was beneath -- harmless over a PDP gallery, but it clipped the store logo on
 * MyStore and the page title on every `sticky top-0` listing/cart header. The fix is that the bar
 * occupies real space and its height is published as `--wb-offer-h`, which the PDP root and
 * PDPShimmer subtract from their viewport heights so the two add up to exactly one viewport and no
 * outer scrollbar appears.
 *
 * ⚠️ UPDATED (prototype): THIS COMPONENT NO LONGER PUBLISHES THAT VARIABLE, and is no longer
 * `sticky` itself. Both moved up to PromoStack.jsx, which wraps this bar plus the coupon bar and
 * measures the WHOLE STACK. There can only be one publisher: the effect that used to live here
 * also REMOVED the property on three paths (!isActive, unmount, every isActive flip), which would
 * have deleted the stack's reservation while the other bar still occupied space. The reasoning
 * below still describes why the mechanism exists — only the owner changed.
 *
 * MEASURED, NOT HARDCODED. An earlier revision assumed ~34px from one mobile viewport; a reserved
 * gap that disagrees with the real bar height is a misalignment on every screen it is wrong about,
 * and text scaling moves it. PromoStack carries that property forward, and unsets the variable
 * when the stack is empty so consumers fall back to `0px`.
 *
 * z-30 (now on PromoStack) sits above page content and above the floating video card's z-20, but
 * below the gallery lightbox portal (z-9999) so opening a video full screen is not interrupted.
 *
 * The countdown comes from the context, which recomputes it from the server's absolute
 * validTillEpochMs on every tick -- never from a local start time. At zero the context clears
 * itself and this renders null, with no reload.
 */
export default function WinBackOfferBanner() {
  const offerCtx = useWinBackOffer();

  // categoryId/categoryName are no longer read here: the redirect moved to the confirmation
  // sheet's Done button, so this bar is copy plus countdown only.
  const { isActive, msLeft, percent } = offerCtx || {};

  // ⚠️ This component no longer publishes `--wb-offer-h`. The effect that did was removed
  // when the coupon bar landed above it: once a second bar shares the stack there can only be
  // one publisher, because that effect also REMOVED the property on three paths (!isActive,
  // unmount, every isActive flip) and would have deleted the whole stack's reservation while
  // the other bar was still occupying space. Measurement belongs to PromoStack.jsx, which
  // measures the stack as a whole. `barRef` went with it.

  // No provider, no offer, or expired -- render nothing at all rather than an empty bar.
  if (!offerCtx || !isActive) {
    return null;
  }

  // No destination (the offer was minted for a product with no hct category) still deserves the
  // countdown -- the discount is real and applies store-wide -- so only the ACTION is dropped.
  return (
    /* `sticky`, NOT `fixed` — this is the overflow fix.
       WHAT WAS CAUSING IT: `fixed left-0 right-0` positions against the VIEWPORT, but the whole
       app renders inside a centred antd `Col` that is narrower than the window at every
       breakpoint above xs (App.js: xxl=14/24 ≈ 58%, xl=18/24, lg/sm=20/24, md=22/24). So the bar
       spanned the full window while the page was a narrow centred column, bleeding past both
       page edges. It was never a stray margin.
       WHY NOT just re-create the column width: antd's breakpoints (sm 576 / md 768 / lg 992 /
       xl 1200 / xxl 1600) do not match Tailwind's (640 / 768 / 1024 / 1280 / 1536), and this
       project only extends `xxs` and `xs`. Any media-query copy of those widths would disagree
       with the actual column at several window sizes.
       `sticky` keeps the bar in normal flow, so it simply inherits the Col's width — "constrained
       to the page like everything else", with no width arithmetic to drift. It still pins to the
       top on scroll. Because it is now in flow it occupies its own space, so the Col's
       `paddingTop: var(--wb-offer-h)` reservation is removed in App.js; `--wb-offer-h` is still
       published below because the PDP root and PDPShimmer subtract it from their viewport
       heights. */
    <div
      role="status"
      aria-live="off"
      /* `sticky top-0 z-30` MOVED TO PromoStack — the stack pins as one unit. Two
         independently sticky children would both pin to offset zero and overlap. */
      className="flex items-center justify-center gap-3 bg-surface-peach border-b border-coral/[0.22] px-3 py-[7px]"
    >
      {/* CEO wording. "Shop now" is gone -- the confirmation sheet's Done button now owns the
          redirect, so a second route to the same place would be two entry points for one action.
          `percent` stays dynamic rather than a hardcoded 5: the server mints the value and the
          client has never been allowed to assume it. */}
      <span className="text-[11.5px] text-ink-900 whitespace-nowrap">
        <strong className="font-[500]">Claim {percent ?? 5}% OFF coupon</strong>
        {" — "}
        <span
          className="font-[500] text-coral-ink tabular-nums"
          /* aria-live is off on the wrapper: a per-second countdown announced by a screen
             reader would be unusable. The offer is announced once when the banner appears. */
        >
          {formatOfferCountdown(msLeft)}
        </span>{" "}
        left
      </span>
    </div>
  );
}
